import { matchPath } from 'react-router-dom'

/**
 * Маршруты, на которых глобальная плашка текущего урока не рисуется.
 *
 * Шаблоны — ровно как в `src/router/AppRouter.tsx`. Сверка — через `matchPath`
 * по всему пути и без учёта регистра, как у самого роутера, а не `startsWith`
 * по строкам: иначе `/lessons/:id` задел бы вложенные страницы урока, а
 * `/exercises/review/:id` — очередь проверки `/exercises/review`.
 *
 * Скрывается только отрисовка: стор и подписки на урок работают и здесь, поэтому
 * при уходе с такой страницы плашка появляется сразу, без нового запроса.
 * Флагов «скрыть плашку» на самих страницах не заводим — список живёт только тут.
 */
export const ACTIVE_LESSON_BAR_HIDDEN_ROUTES: readonly string[] = [
  // Страницы, которые сами «про урок» или требуют всего экрана: урок, создание
  // теста и заполнения словаря к уроку, прохождение теста, заполнения словаря и
  // задания, проверка задания и домашки, тест уровня. Вернуться на урок отсюда
  // некуда, а плашка перекрывала бы работу. Неважно, тот ли это урок: тест
  // другого урока она перекрывает так же.
  //
  // Новая страница прохождения или проверки — добавить её шаблон сюда: это часть
  // работы по созданию страницы.
  '/lessons/:id',
  '/lessons/:lessonId/tests/create',
  '/lessons/:lessonId/dictionary-fill/create',
  '/tests/:testId',
  '/dictionary-fill/:runId',
  '/exercise-runs/:id',
  '/exercises/review/:id',
  '/users/:userId/homeworks/:homeworkId/items/:itemId/check',
  '/placement',

  // Страницы вне кабинета: лендинг, вход и регистрация, сброс пароля, согласие
  // на подключение AI-агента и полноэкранный мастер онбординга (тот же тест
  // уровня без кабинета). Авторизованный пользователь может на них оказаться, но
  // плашка — элемент кабинета, а каркаса с сайдбаром здесь нет.
  // `/news` и `/news/:slug` сюда не входят: авторизованному они рисуются внутри
  // кабинета, а гость плашку не получает вовсе. Страница 404 (`*`) шаблоном не
  // выражается и остаётся с плашкой — с битой ссылки это путь назад на урок.
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/oauth/authorize',
  '/onboarding',
]

/**
 * Отдельно объявленные маршруты, которые похожи на шаблон из списка выше, но
 * страницей прохождения не являются. Роутер отдаёт статический сегмент раньше
 * параметра, и сверка повторяет это явно: `/tests/self/create` — создание
 * самостоятельного теста, а не прохождение теста `/tests/:testId`.
 */
export const ACTIVE_LESSON_BAR_VISIBLE_ROUTES: readonly string[] = ['/tests/self/create']

/** Плашку на этом пути не рисуем. */
export function isActiveLessonBarHiddenPath(pathname: string): boolean {
  if (ACTIVE_LESSON_BAR_VISIBLE_ROUTES.some((pattern) => matchPath(pattern, pathname))) {
    return false
  }
  return ACTIVE_LESSON_BAR_HIDDEN_ROUTES.some((pattern) => matchPath(pattern, pathname))
}
